/**
 * 🧠 useEmotionalMode Hook
 * 
 * Provides emotional mode status across all modes (Work, Health, Life)
 * 
 * RULES:
 * - When emotional mode is active, suppress:
 *   - Streak pressure
 *   - Overdue panic
 *   - Guilt language
 *   - Urgent reminders
 * - Always prioritize supportive messages over metrics
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useEmotionalMode = () => {
  const [emotionalMode, setEmotionalMode] = useState(false);
  const [ambientEmotional, setAmbientEmotional] = useState(false);
  const [suppressHealth, setSuppressHealth] = useState(false);
  const [suppressStreaks, setSuppressStreaks] = useState(false);
  const [recommendedTone, setRecommendedTone] = useState('neutral');
  const [loading, setLoading] = useState(true);

  const fetchEmotionalState = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/home/smart-dashboard`, {
        credentials: 'include'
      });
      
      // If not authenticated, just set defaults and return
      if (response.status === 401) {
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        const dashboard = data.dashboard || {};
        
        setEmotionalMode(dashboard.emotional_mode || false);
        setAmbientEmotional(dashboard.ambient_emotional || false);
        setSuppressHealth(dashboard.suppress_health_ui || false);
        setSuppressStreaks(dashboard.emotional_mode || dashboard.ambient_emotional || false);
        setRecommendedTone(dashboard.recommended_tone || 'neutral');
      }
    } catch (error) {
      // Silently fail - don't log expected 401 errors
      if (!error.message?.includes('401')) {
        console.error('Error fetching emotional state:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmotionalState();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchEmotionalState, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEmotionalState]);

  // Helper to get appropriate tone for messages
  const getToneMessage = (normalMsg, supportiveMsg, gentleMsg) => {
    if (emotionalMode || ambientEmotional) {
      return supportiveMsg || gentleMsg;
    }
    if (recommendedTone === 'gentle') {
      return gentleMsg || supportiveMsg;
    }
    return normalMsg;
  };

  // Check if streaks should be shown
  const shouldShowStreaks = () => {
    return !suppressStreaks && !emotionalMode && !ambientEmotional;
  };

  // Check if metrics should be emphasized
  const shouldEmphasizeMetrics = () => {
    return !suppressHealth && !emotionalMode && !ambientEmotional;
  };

  // Check if urgent/overdue language is appropriate
  const shouldShowUrgentLanguage = () => {
    return !emotionalMode && !ambientEmotional && recommendedTone === 'neutral';
  };

  return {
    // State
    emotionalMode,
    ambientEmotional,
    suppressHealth,
    suppressStreaks,
    recommendedTone,
    loading,
    
    // Helpers
    getToneMessage,
    shouldShowStreaks,
    shouldEmphasizeMetrics,
    shouldShowUrgentLanguage,
    
    // Actions
    refresh: fetchEmotionalState
  };
};

export default useEmotionalMode;
